import React, { useState, useMemo, useContext } from "react";
import { HolderOutlined } from "@ant-design/icons";
import { Button, Col, Modal, Row, Table, theme, Typography } from "antd";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { fromJS } from "immutable";

const ReorderModal = ({
  open,
  closeModal,
  modalTitle,
  description,
  columnTitle,
  data,
  handleSaveReorder,
  dataIndex,
  dataKey,
  convertToImmutable,
}) => {
  const [dataSource, setDataSource] = useState(data ?? []);
  const { token } = theme.useToken();
  const RowContext = React.createContext({});

  const DragHandle = () => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext);
    return (
      <Button
        type="text"
        size="small"
        icon={<HolderOutlined />}
        style={{ cursor: "move" }}
        ref={setActivatorNodeRef}
        {...listeners}
      />
    );
  };

  const columns = [
    { key: "sort", align: "center", width: 56, render: () => <DragHandle /> },
    {
      title: columnTitle,
      dataIndex: dataIndex,
      key: dataKey,
      render: (_, record) => record[dataKey],
    },
  ];

  const RowProperty = (props) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: props["data-row-key"] });

    const style = {
      ...props.style,
      transform: CSS.Translate.toString(transform),
      transition,
      ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
    };

    const contextValue = useMemo(
      () => ({ setActivatorNodeRef, listeners }),
      [setActivatorNodeRef, listeners]
    );

    return (
      <RowContext.Provider value={contextValue}>
        <tr {...props} ref={setNodeRef} style={style} {...attributes} />
      </RowContext.Provider>
    );
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = dataSource.findIndex((item) => item.id === active.id);
    const newIndex = dataSource.findIndex((item) => item.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reordered = arrayMove(dataSource, oldIndex, newIndex);
      setDataSource(reordered);
    }
  };

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={closeModal}
      onOk={() => {
        handleSaveReorder(convertToImmutable ? fromJS(dataSource) : dataSource);
        closeModal();
      }}
      okText="Save"
      width={859}
    >
      <Row gutter={[0, token.margin]}>
        <Col span={24}>
          <Typography.Text type="secondary">{description}</Typography.Text>
        </Col>
        <Col span={24}>
          <DndContext
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={dataSource.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <Table
                rowKey={(record) => record.id}
                components={{ body: { row: RowProperty } }}
                columns={columns}
                dataSource={dataSource}
                pagination={false}
                sticky
                scroll={{ y: 400 }}
              />
            </SortableContext>
          </DndContext>
        </Col>
      </Row>
    </Modal>
  );
};

export default ReorderModal;
